// Typed client + TanStack Query hooks for the catalog module (PRODUCT-001, MEDIA-001, SEARCH-001).
// Shapes mirror packages/domain/{product,media,search}/src/http.ts exactly; the mocks in
// src/mocks/modules/catalog.ts return the same shapes. Money is integer paisa everywhere.
import { useQuery } from "@tanstack/react-query";
import { API_BASE, ApiError, request, reviveMocks } from "../api/client";

export type ProductStatus = "draft" | "active" | "unlisted" | "archived";
export type Channel = "web" | "pos" | "headless";
export type PurchasableMode = "buy" | "coming_soon" | "price_on_request" | "external";
export type FieldType =
  "text" | "textarea" | "number" | "select" | "multi" | "date" | "slot" | "image";

export interface Page<T> {
  data: T[];
  next_cursor: string | null;
}

export interface ProductSummary {
  id: string;
  title: string;
  slug: string;
  status: ProductStatus;
  channels: Channel[];
  price_min_paisa: number;
  price_max_paisa: number;
  quality_score: number;
  updated_at: string;
  version: number;
  variant_count: number;
  image: { asset_id: string; preview_path: string } | null;
  /** Present only with products.cost.read. */
  cost_min_paisa?: number | null;
  cost_max_paisa?: number | null;
}

export interface Variant {
  id: string;
  title: string;
  options: Record<string, string>;
  sku: string | null;
  barcode: string | null;
  hs_code: string | null;
  price_paisa: number;
  compare_at_paisa: number | null;
  cost_paisa?: number | null;
  weight_g: number | null;
  image_asset_id: string | null;
  alt_barcodes?: string[];
  active: boolean;
  position: number;
  version: number;
}

export interface CustomField {
  id?: string;
  type: FieldType;
  label: string;
  required: boolean;
  options: { value: string; label?: string; surcharge_paisa?: number }[];
  surcharge_paisa: number;
  config: Record<string, unknown>;
}

export interface Axis {
  name: string;
  values: string[];
  /** 1022: colour preview per value: one "#RRGGBB", or two for a split swatch. */
  swatches?: Record<string, string[]>;
}

export interface Product {
  id: string;
  store_id: string;
  title: string;
  slug: string;
  description_html: string;
  status: ProductStatus;
  channels: Channel[];
  purchasable_mode: PurchasableMode;
  external_url: string | null;
  brand_id: string | null;
  category_ids: string[];
  tags: string[];
  features: string[];
  specs: { group?: string; name: string; value: string }[];
  seo: { title?: string; description?: string; image_asset_id?: string | null };
  tax_class: "standard" | "exempt" | "zero";
  track_inventory: boolean;
  allow_backorder: boolean;
  options: Axis[];
  variants: Variant[];
  media: { asset_id: string; alt: string; preview_path: string }[];
  /** 1023 uploaded videos. */
  videos?: ProductVideo[];
  custom_fields: CustomField[];
  related_ids: string[];
  delivery_profile_id: string | null;
  preferred_courier: string | null;
  payment_exclusions: string[];
  long_description_html?: string;
  release_date?: string | null;
  video_url?: string | null;
  features_heading?: string | null;
  free_delivery?: boolean;
  delivery_time?: string | null;
  installation_preset_id?: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface VariantInput {
  options: Record<string, string>;
  price_paisa: number;
  compare_at_paisa?: number | null;
  cost_paisa?: number | null;
  sku?: string | null;
  barcode?: string | null;
  hs_code?: string | null;
  weight_g?: number | null;
  image_asset_id?: string | null;
  alt_barcodes?: string[];
}

export interface ProductWrite {
  title?: string;
  description_html?: string;
  status?: ProductStatus;
  channels?: Channel[];
  purchasable_mode?: PurchasableMode;
  external_url?: string | null;
  brand_id?: string | null;
  category_ids?: string[];
  tags?: string[];
  seo?: { title?: string; description?: string; image_asset_id?: string | null };
  tax_class?: "standard" | "exempt" | "zero";
  track_inventory?: boolean;
  allow_backorder?: boolean;
  media?: { asset_id: string; alt?: string }[];
  videos?: { asset_id: string; poster_asset_id?: string | null; duration_ms?: number | null }[];
  features?: string[];
  specs?: { group?: string; name: string; value: string }[];
  related_ids?: string[];
  payment_exclusions?: string[];
  delivery_profile_id?: string | null;
  preferred_courier?: string | null;
  long_description_html?: string;
  release_date?: string | null;
  video_url?: string | null;
  features_heading?: string | null;
  free_delivery?: boolean;
  delivery_time?: string | null;
  installation_preset_id?: string | null;
}

export interface ProductCreate extends ProductWrite {
  title: string;
  options: Axis[];
  variants: VariantInput[];
  custom_fields?: CustomField[];
}

export interface Category {
  id: string;
  parent_id: string | null;
  path: string;
  name: string;
  slug: string;
  position: number;
  product_count: number;
  /** 1026 details (older mock rows may lack them). */
  description?: string;
  image_asset_id?: string | null;
  image_alt?: string;
  active?: boolean;
  hide_on_product?: boolean;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string[];
  seo_image_asset_id?: string | null;
  version?: number;
  created_at?: string;
  updated_at?: string;
}
/** Create and edit body (1026). */
export interface CategoryWrite {
  name: string;
  parent_id?: string | null;
  description?: string;
  image_asset_id?: string | null;
  image_alt?: string;
  active?: boolean;
  hide_on_product?: boolean;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string[];
  seo_image_asset_id?: string | null;
}
export interface Brand {
  id: string;
  name: string;
  slug: string;
  logo_asset_id: string | null;
  product_count: number;
  /** 1025 details (older mock rows may lack them). */
  active?: boolean;
  position?: number;
  description?: string;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_image_asset_id?: string | null;
  version?: number;
  created_at?: string;
  updated_at?: string;
}
/** Create and edit body (1025). */
export interface BrandWrite {
  name: string;
  logo_asset_id?: string | null;
  active?: boolean;
  description?: string;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_image_asset_id?: string | null;
}
export type RuleCondition =
  | { field: "tag" | "category" | "brand"; op: "in"; value: string[] }
  | { field: "price"; op: "gte" | "lte"; value: number }
  | { field: "discounted"; op: "is"; value: boolean }
  | { field: "title"; op: "contains"; value: string };
export interface CollectionRule {
  match: "all" | "any";
  conditions: RuleCondition[];
}
export interface Collection {
  id: string;
  slug: string;
  title: string;
  description: string;
  kind: "manual" | "rule";
  rule: CollectionRule | null;
  sort: CollectionSort;
  banner_asset_id: string | null;
  /** 1024 details (older mock rows may lack them). */
  active?: boolean;
  position?: number;
  video_asset_id?: string | null;
  video_poster_asset_id?: string | null;
  coupon_id?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string[];
  seo_image_asset_id?: string | null;
  version: number;
  product_count: number;
  created_at: string;
  updated_at: string;
  product_ids?: string[];
}
export type CollectionSort = "manual" | "newest" | "price_asc" | "price_desc" | "title";
/** Create and edit body (POST and PATCH; PATCH omits kind). */
export interface CollectionWrite {
  title: string;
  description?: string;
  kind?: "manual" | "rule";
  rule?: CollectionRule | null;
  product_ids?: string[];
  sort?: CollectionSort;
  banner_asset_id?: string | null;
  active?: boolean;
  video_asset_id?: string | null;
  video_poster_asset_id?: string | null;
  coupon_id?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string[];
  seo_image_asset_id?: string | null;
}

export type BulkActionBody =
  | { action: "set_status"; params: { status: ProductStatus } }
  | { action: "set_price"; params: { mode: "set" | "percent" | "delta"; value: number } }
  | { action: "add_tags" | "remove_tags"; params: { tags: string[] } };

export interface BulkJob {
  id: string;
  action: string;
  params: unknown;
  state: "queued" | "running" | "done" | "failed";
  total: number;
  succeeded: number;
  failed: number;
  created_at: string;
  finished_at: string | null;
  items: {
    product_id: string;
    outcome: "pending" | "ok" | "skipped" | "failed";
    error_code: string | null;
  }[];
}

/** A library folder (owner, 2026-10-22): the four every store has, and one per product using media. */
export interface MediaFolder {
  id: string;
  kind: "system" | "product";
  /** The product's title; system folders are named in the client. */
  name: string | null;
  product_id: string | null;
  count: number;
  cover_path: string | null;
}

export interface MediaAsset {
  id: string;
  store_id: string | null;
  purpose: string;
  state: "pending" | "ready" | "rejected" | "deleted";
  filename: string;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  alt: string;
  folder_id: string | null;
  usages: number;
  renditions: {
    variant: string;
    format: string;
    width: number | null;
    height: number | null;
    bytes: number;
    ready: boolean;
  }[];
  preview_path: string | null;
  public_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductVideo {
  asset_id: string;
  poster_asset_id: string | null;
  duration_ms: number | null;
  /** Admin file route of the video (plays with seeking). */
  file_path: string;
  poster_path: string | null;
}

export interface UploadRequest {
  purpose: "product" | "product_video" | "kyc";
  filename: string;
  content_type: string;
  size: number;
  sha256?: string;
  width?: number;
  height?: number;
  alt?: string;
  store_id?: string;
  renditions: {
    variant: "w320" | "w640" | "w1080" | "w1600";
    format: "webp" | "jpeg" | "png" | "avif";
    size: number;
    width: number;
  }[];
}
export interface UploadTickets {
  asset_id: string;
  asset: MediaAsset;
  uploads: { variant: string; format: string; url: string; expires_at: string }[];
}

const s = (org: string, store: string) =>
  `/orgs/${encodeURIComponent(org)}/stores/${encodeURIComponent(store)}`;
const o = (org: string) => `/orgs/${encodeURIComponent(org)}`;

export const catalogApi = {
  products: (
    org: string,
    store: string,
    q: { status?: string; q?: string; cursor?: string; limit?: number },
  ) => request<Page<ProductSummary>>("GET", `${s(org, store)}/products`, { query: q }),
  product: (org: string, store: string, id: string) =>
    request<Product>("GET", `${s(org, store)}/products/${id}`),
  createProduct: (org: string, store: string, body: ProductCreate, idempotencyKey: string) =>
    request<Product>("POST", `${s(org, store)}/products`, { body, idempotencyKey }),
  updateProduct: (org: string, store: string, id: string, body: ProductWrite, version: number) =>
    request<Product>("PATCH", `${s(org, store)}/products/${id}`, { body, ifMatch: version }),
  deleteProduct: (org: string, store: string, id: string) =>
    request<void>("DELETE", `${s(org, store)}/products/${id}`),
  duplicate: (org: string, store: string, id: string) =>
    request<Product>("POST", `${s(org, store)}/products/${id}/duplicate`, {
      body: {},
      idempotent: true,
    }),
  generateVariants: (
    org: string,
    store: string,
    id: string,
    body: {
      axes: Axis[];
      selected_combinations: Record<string, string>[];
      defaults?: { price_paisa?: number };
    },
    version: number,
  ) =>
    request<Product>("POST", `${s(org, store)}/products/${id}/variants/generate`, {
      body,
      ifMatch: version,
    }),
  updateVariant: (
    org: string,
    store: string,
    id: string,
    body: Partial<VariantInput> & { active?: boolean },
    version: number,
  ) =>
    request<Variant & { product_version: number }>("PATCH", `${s(org, store)}/variants/${id}`, {
      body,
      ifMatch: version,
    }),
  putCustomFields: (
    org: string,
    store: string,
    id: string,
    fields: CustomField[],
    version: number,
  ) =>
    request<{ data: CustomField[]; version: number }>(
      "PUT",
      `${s(org, store)}/products/${id}/custom-fields`,
      {
        body: { fields },
        ifMatch: version,
      },
    ),
  bulk: (
    org: string,
    store: string,
    body: { ids: string[] } & BulkActionBody,
    idempotencyKey: string,
  ) =>
    request<{ job_id: string; total: number; state: string }>(
      "POST",
      `${s(org, store)}/products/bulk`,
      { body, idempotencyKey },
    ),
  bulkJob: (org: string, store: string, id: string) =>
    request<BulkJob>("GET", `${s(org, store)}/bulk-jobs/${id}`),

  categories: (org: string, store: string) =>
    request<Page<Category>>("GET", `${s(org, store)}/categories`),
  createCategory: (org: string, store: string, body: CategoryWrite, idempotencyKey: string) =>
    request<Category>("POST", `${s(org, store)}/categories`, { body, idempotencyKey }),
  updateCategory: (
    org: string,
    store: string,
    id: string,
    version: number,
    body: Partial<CategoryWrite>,
  ) => request<Category>("PATCH", `${s(org, store)}/categories/${id}`, { body, ifMatch: version }),
  /** 1026: one parent's subcategory order (null = top level). */
  reorderCategories: (org: string, store: string, parentId: string | null, ids: string[]) =>
    request<Page<Category>>("PUT", `${s(org, store)}/categories/order`, {
      body: { parent_id: parentId, ids },
    }),
  deleteCategory: (org: string, store: string, id: string) =>
    request<void>("DELETE", `${s(org, store)}/categories/${id}`),
  brands: (org: string, store: string) => request<Page<Brand>>("GET", `${s(org, store)}/brands`),
  createBrand: (org: string, store: string, body: BrandWrite, idempotencyKey: string) =>
    request<Brand>("POST", `${s(org, store)}/brands`, { body, idempotencyKey }),
  updateBrand: (
    org: string,
    store: string,
    id: string,
    version: number,
    body: Partial<BrandWrite>,
  ) => request<Brand>("PATCH", `${s(org, store)}/brands/${id}`, { body, ifMatch: version }),
  /** 1025: the display order. */
  reorderBrands: (org: string, store: string, ids: string[]) =>
    request<Page<Brand>>("PUT", `${s(org, store)}/brands/order`, { body: { ids } }),
  deleteBrand: (org: string, store: string, id: string) =>
    request<void>("DELETE", `${s(org, store)}/brands/${id}`),
  collections: (org: string, store: string) =>
    request<Page<Collection>>("GET", `${s(org, store)}/collections`),
  createCollection: (
    org: string,
    store: string,
    body: CollectionWrite & { kind: "manual" | "rule" },
    idempotencyKey: string,
  ) => request<Collection>("POST", `${s(org, store)}/collections`, { body, idempotencyKey }),
  collection: (org: string, store: string, id: string) =>
    request<Collection>("GET", `${s(org, store)}/collections/${id}`),
  updateCollection: (
    org: string,
    store: string,
    id: string,
    version: number,
    body: Omit<CollectionWrite, "kind">,
  ) =>
    request<Collection>("PATCH", `${s(org, store)}/collections/${id}`, {
      body,
      ifMatch: version,
    }),
  /** 1024: the display order. */
  reorderCollections: (org: string, store: string, ids: string[]) =>
    request<Page<Collection>>("PUT", `${s(org, store)}/collections/order`, { body: { ids } }),
  deleteCollection: (org: string, store: string, id: string) =>
    request<void>("DELETE", `${s(org, store)}/collections/${id}`),

  media: (
    org: string,
    q: { cursor?: string; limit?: number; purpose?: string; group?: string; store?: string } = {},
  ) => request<Page<MediaAsset>>("GET", `${o(org)}/media`, { query: q }),
  mediaFolders: (org: string, store: string) =>
    request<{ data: MediaFolder[] }>("GET", `${o(org)}/media/folders`, { query: { store } }),
  mediaUsage: (org: string) =>
    request<{ used_bytes: number; limit_bytes: number | null; plan: string }>(
      "GET",
      `${o(org)}/media/usage`,
    ),
  createUpload: (org: string, body: UploadRequest, idempotencyKey: string) =>
    request<UploadTickets>("POST", `${o(org)}/media/uploads`, { body, idempotencyKey }),
  completeUpload: (org: string, id: string) =>
    request<MediaAsset>("POST", `${o(org)}/media/${id}/complete`, { body: {} }),
  updateMedia: (org: string, id: string, body: { alt?: string }) =>
    request<MediaAsset>("PATCH", `${o(org)}/media/${id}`, { body }),
  deleteMedia: (org: string, id: string, force = false) =>
    request<void>("DELETE", `${o(org)}/media/${id}`, { query: force ? { force: 1 } : {} }),
  // 1022 installation services (SHIPPING-001)
  installations: (org: string, store: string) =>
    request<Page<Installation>>("GET", `${s(org, store)}/installation-presets`),
  createInstallation: (
    org: string,
    store: string,
    body: InstallationWrite,
    idempotencyKey: string,
  ) =>
    request<Installation>("POST", `${s(org, store)}/installation-presets`, {
      body,
      idempotencyKey,
    }),
  updateInstallation: (
    org: string,
    store: string,
    id: string,
    body: Partial<InstallationWrite>,
    version: number,
  ) =>
    request<Installation>("PATCH", `${s(org, store)}/installation-presets/${id}`, {
      body,
      ifMatch: version,
    }),
  deleteInstallation: (org: string, store: string, id: string, version: number) =>
    request<void>("DELETE", `${s(org, store)}/installation-presets/${id}`, { ifMatch: version }),
  /** AI-02 draft for a product being added (nothing is saved; the text goes into the form). */
  aiDescription: (
    org: string,
    store: string,
    draft: { title: string; description: string; features: string[]; tags: string[] },
  ) =>
    request<{ draft: { text: string; description_html: string } }>(
      "POST",
      `${s(org, store)}/ai/generate`,
      { body: { kind: "description", input: { draft, tone: "friendly", notes: "" } } },
    ),
};

export interface InstallationPriceRule {
  type: "province" | "district" | "local_level";
  code: string;
  price_paisa: number;
  label?: string;
}
export interface Installation {
  id: string;
  store_id: string;
  name: string;
  description: string;
  prices: InstallationPriceRule[];
  default_price_paisa: number | null;
  time_text: string | null;
  active: boolean;
  product_count: number | null;
  version: number;
  created_at: string;
  updated_at: string;
}
export interface InstallationWrite {
  name: string;
  description: string;
  prices: { type: InstallationPriceRule["type"]; code: string; price_paisa: number }[];
  default_price_paisa: number | null;
  time_text: string | null;
  active: boolean;
}

/** PUT one object to its signed ticket URL (the URL comes from the server; never built here). */
export async function putObject(url: string, blob: Blob): Promise<void> {
  const path = url.startsWith(API_BASE) ? url : `${API_BASE}${url}`;
  let res: Response;
  const send = () =>
    fetch(path, {
      method: "PUT",
      headers: { "Content-Type": blob.type, "X-Ady-Client": "1" },
      credentials: "same-origin",
      body: blob,
    });
  try {
    res = await send();
    if (await reviveMocks(res)) res = await send();
  } catch {
    throw new ApiError(0, "network", undefined, undefined, undefined, undefined);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { code?: string; request_id?: string };
    } | null;
    throw new ApiError(
      res.status,
      (body?.error?.code ?? "internal") as "internal",
      body?.error?.request_id,
      undefined,
      undefined,
      undefined,
    );
  }
}

export const catalogKeys = {
  products: (org: string, store: string, q: unknown) =>
    ["org", org, "store", store, "products", q] as const,
  productsAll: (org: string, store: string) => ["org", org, "store", store, "products"] as const,
  product: (org: string, store: string, id: string) =>
    ["org", org, "store", store, "product", id] as const,
  categories: (org: string, store: string) => ["org", org, "store", store, "categories"] as const,
  brands: (org: string, store: string) => ["org", org, "store", store, "brands"] as const,
  collections: (org: string, store: string) => ["org", org, "store", store, "collections"] as const,
  installations: (org: string, store: string) =>
    ["org", org, "store", store, "installations"] as const,
  media: (org: string) => ["org", org, "media"] as const,
  mediaUsage: (org: string) => ["org", org, "media-usage"] as const,
  mediaFolders: (org: string, store: string) => ["org", org, "media", "folders", store] as const,
  mediaFolder: (org: string, store: string, group: string) =>
    ["org", org, "media", "group", store, group] as const,
  bulkJob: (org: string, store: string, id: string) =>
    ["org", org, "store", store, "bulk", id] as const,
};

export const useCategories = (org: string, store: string, enabled = true) =>
  useQuery({
    queryKey: catalogKeys.categories(org, store),
    queryFn: () => catalogApi.categories(org, store),
    enabled,
  });
export const useInstallations = (org: string, store: string, enabled = true) =>
  useQuery({
    queryKey: catalogKeys.installations(org, store),
    queryFn: () => catalogApi.installations(org, store),
    enabled,
  });
export const useBrands = (org: string, store: string, enabled = true) =>
  useQuery({
    queryKey: catalogKeys.brands(org, store),
    queryFn: () => catalogApi.brands(org, store),
    enabled,
  });
export const useCollections = (org: string, store: string, enabled = true) =>
  useQuery({
    queryKey: catalogKeys.collections(org, store),
    queryFn: () => catalogApi.collections(org, store),
    enabled,
  });
/** A pending activation (503 dependency.activation_required) is a state, not a failure: never retried. */
const noRetryPending = (count: number, err: unknown) =>
  !(
    err instanceof ApiError &&
    (err.code === "dependency.activation_required" || (err.status >= 400 && err.status < 500))
  ) && count < 2;

export const useMediaLibrary = (org: string, enabled = true) =>
  useQuery({
    queryKey: catalogKeys.media(org),
    queryFn: () => catalogApi.media(org, { limit: 60, purpose: "product" }),
    enabled,
    retry: noRetryPending,
  });
export const useMediaUsage = (org: string, enabled = true) =>
  useQuery({
    queryKey: catalogKeys.mediaUsage(org),
    queryFn: () => catalogApi.mediaUsage(org),
    enabled,
    retry: noRetryPending,
  });

/** Maps catalog validation errors to field copy keys. */
export function fieldOf(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.fields) return null;
  return Object.keys(err.fields)[0] ?? null;
}
